import React, { useCallback, useEffect, useRef, useState } from "react";
import { fabric } from "utils/fabric";
import {
  inchesToUiPx,
  getDesignGapPx,
  autoArrangeObjects,
  hasOverlappingDesigns,
  uiPxToInches,
  estimateDpi,
} from "pages/dtf-transfers/gang-sheet/helpers";
import { CanvasShell, CanvasInner, CanvasInnerContainer } from "styles/pages/dtf-transfers/gang-sheet";

const CANVAS_ID = "dtf-gang-sheet-canvas";

const styleDesignObject = (obj) => {
  obj.set({
    transparentCorners: false,
    cornerColor: "#1594db",
    cornerStrokeColor: "#fff",
    borderColor: "#1594db",
    cornerStyle: "circle",
    cornerSize: 10,
  });
  obj.setControlsVisibility({
    tl: true,
    tr: true,
    br: true,
    bl: true,
    ml: false,
    mt: false,
    mr: false,
    mb: false,
    mtr: true,
  });
};

const loadFabricImage = (url) => {
  return new Promise((resolve, reject) => {
    const htmlImg = new Image();
    htmlImg.onload = () => {
      const fabImg = new fabric.Image(htmlImg);
      resolve(fabImg);
    };
    htmlImg.onerror = () => {
      reject(new Error("Failed to load image: " + url));
    };
    htmlImg.crossOrigin = "anonymous";
    htmlImg.src = url;
  });
};

const GangCanvas = ({
  sheetWidth,
  sheetHeight,
  zoom,
  canvasRef,
  onSelectionChange,
  onOverlapChange,
  onHistoryPush,
  onObjectsChange,
  onReady,
}) => {
  const fabricRef = useRef(null);
  const [ready, setReady] = useState(false);

  const widthPx = inchesToUiPx(sheetWidth);
  const heightPx = inchesToUiPx(sheetHeight);

  const pushHistory = useCallback(() => {
    if (!fabricRef.current || !onHistoryPush) return;
    onHistoryPush(JSON.stringify(fabricRef.current.toJSON(["designMeta", "isGangDesign"])));
  }, [onHistoryPush]);

  const checkOverlap = useCallback(() => {
    if (!fabricRef.current || !onOverlapChange) return;
    const designs = fabricRef.current
      .getObjects()
      .filter((obj) => obj.isGangDesign || obj.designMeta);
    onOverlapChange(hasOverlappingDesigns(designs));
  }, [onOverlapChange]);

  useEffect(() => {
    const canvas = new fabric.Canvas(CANVAS_ID, {
      width: widthPx,
      height: heightPx,
      backgroundColor: "transparent",
      preserveObjectStacking: true,
      selection: true,
    });

    fabricRef.current = canvas;
    if (canvasRef) {
      canvasRef.current = canvas;
    }

    const onSelect = () => {
      const active = canvas.getActiveObject();
      const isDesign =
        active?.isGangDesign ||
        active?.designMeta ||
        (active &&
          (String(active.type || "").toLowerCase() === "image" ||
            Boolean(active._element || active.getSrc)));
      onSelectionChange?.(isDesign ? active : null);
    };

    const onCanvasChanged = () => {
      checkOverlap();
      onObjectsChange?.(canvas);
    };

    const onModified = () => {
      onCanvasChanged();
      pushHistory();
      onSelect();
    };

    canvas.on("selection:created", onSelect);
    canvas.on("selection:updated", onSelect);
    canvas.on("selection:cleared", () => onSelectionChange?.(null));
    canvas.on("object:modified", onModified);
    canvas.on("object:added", onCanvasChanged);
    canvas.on("object:removed", onCanvasChanged);
    canvas.on("object:scaling", (e) => {
      const obj = e.target;
      if (obj && obj.isGangDesign) {
        const maxAllowedWidthPx = widthPx - getDesignGapPx() * 2;
        const currentWidthPx = obj.width * obj.scaleX;
        if (currentWidthPx > maxAllowedWidthPx) {
          obj.scaleX = maxAllowedWidthPx / obj.width;
          obj.scaleY = obj.scaleX;
        }
      }
      onCanvasChanged();
    });

    setReady(true);
    pushHistory();
    onReady?.(canvas);

    return () => {
      canvas.dispose();
      fabricRef.current = null;
      if (canvasRef) canvasRef.current = null;
    };
    // Mount once; parent remounts via key when sheet size changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    canvas.setDimensions({ width: widthPx, height: heightPx });
    canvas.backgroundColor = "transparent";
    canvas.requestRenderAll();
    checkOverlap();
  }, [widthPx, heightPx, checkOverlap]);

  useEffect(() => {
    if (!canvasRef) return undefined;
    canvasRef.current = fabricRef.current;
    return undefined;
  }, [canvasRef, ready]);

  return (
    <CanvasShell>
      <CanvasInnerContainer
        $width={widthPx * zoom}
        $height={heightPx * zoom}
      >
        <CanvasInner
          $zoom={zoom}
          style={{
            width: `${widthPx}px`,
            height: `${heightPx}px`,
          }}
        >
          <canvas id={CANVAS_ID} />
        </CanvasInner>
      </CanvasInnerContainer>
    </CanvasShell>
  );
};

export const addDesignToCanvas = async (
  canvas,
  { url, fileName, publicId, secureUrl, width, height, id },
  sheetWidth,
) => {
  if (!canvas || !url) return null;

  const img = await loadFabricImage(url);
  const maxWidthPx = inchesToUiPx(Math.min(sheetWidth, 5));
  const scale = maxWidthPx / (img.width || 1);

  img.set({
    left: getDesignGapPx(),
    top: getDesignGapPx(),
    scaleX: scale,
    scaleY: scale,
    isGangDesign: true,
    designMeta: {
      id: id || `${publicId}_${Date.now()}_${Math.random()}`,
      fileName,
      publicId,
      secureUrl: secureUrl || url,
      originalSecureUrl: secureUrl || url,
      removeBackground: false,
      upscale: false,
      halftone: false,
      width: width || img.width,
      height: height || img.height,
    },
  });
  styleDesignObject(img);
  canvas.add(img);
  canvas.setActiveObject(img);
  canvas.requestRenderAll();
  return img;
};

export const replaceSelectedImageSrc = async (canvas, object, nextUrl) => {
  if (!canvas || !object || !nextUrl) return null;

  const left = object.left;
  const top = object.top;
  const scaleX = object.scaleX;
  const scaleY = object.scaleY;
  const angle = object.angle;
  const meta = {
    ...object.designMeta,
    secureUrl: nextUrl,
  };

  const img = await loadFabricImage(nextUrl);
  img.set({
    left,
    top,
    scaleX,
    scaleY,
    angle,
    isGangDesign: true,
    designMeta: meta,
  });
  styleDesignObject(img);
  canvas.remove(object);
  canvas.add(img);
  canvas.setActiveObject(img);
  canvas.requestRenderAll();
  return img;
};

export const duplicateActiveObject = async (canvas) => {
  const active = canvas?.getActiveObject();
  if (!active || !(active.designMeta || active.isGangDesign)) return null;

  const cloned = await active.clone(["designMeta", "isGangDesign"]);
  cloned.set({
    left: (active.left || 0) + 16,
    top: (active.top || 0) + 16,
    isGangDesign: true,
    designMeta: { ...active.designMeta },
  });
  styleDesignObject(cloned);
  canvas.add(cloned);
  canvas.setActiveObject(cloned);
  canvas.requestRenderAll();
  return cloned;
};

export const deleteActiveObject = (canvas) => {
  const active = canvas?.getActiveObject();
  if (!active || !(active.designMeta || active.isGangDesign)) return;
  canvas.remove(active);
  canvas.discardActiveObject();
  canvas.requestRenderAll();
};

export const bringForwardActive = (canvas) => {
  const active = canvas?.getActiveObject();
  if (!active) return;
  if (typeof canvas.bringObjectForward === "function") {
    canvas.bringObjectForward(active);
  } else if (typeof canvas.bringForward === "function") {
    canvas.bringForward(active);
  }
  canvas.requestRenderAll();
};

export const sendBackActive = (canvas) => {
  const active = canvas?.getActiveObject();
  if (!active) return;
  if (typeof canvas.sendObjectBackwards === "function") {
    canvas.sendObjectBackwards(active);
  } else if (typeof canvas.sendBackwards === "function") {
    canvas.sendBackwards(active);
  }
  canvas.requestRenderAll();
};

const isFabricImageObject = (obj) => {
  if (!obj) return false;
  const type = String(obj.type || "").toLowerCase();
  return type === "image" || Boolean(obj._element || obj.getSrc);
};

export const getGangDesignObjects = (canvas) => {
  if (!canvas) return [];
  return canvas
    .getObjects()
    .filter(
      (obj) =>
        obj?.isGangDesign || obj?.designMeta || isFabricImageObject(obj),
    );
};

export const runAutoArrange = (canvas, sheetWidth, sheetHeight) => {
  if (!canvas) return;
  const designs = getGangDesignObjects(canvas);
  autoArrangeObjects(
    designs,
    inchesToUiPx(sheetWidth),
    inchesToUiPx(sheetHeight),
    getDesignGapPx(),
  );
  canvas.requestRenderAll();
};

const restoreGangDesignProps = (serialized, obj) => {
  if (!obj) return;

  if (serialized?.designMeta) {
    obj.set("designMeta", serialized.designMeta);
  }
  if (
    serialized?.isGangDesign ||
    serialized?.designMeta ||
    isFabricImageObject(obj)
  ) {
    obj.set("isGangDesign", true);
    if (!obj.designMeta && serialized?.src) {
      obj.set("designMeta", {
        secureUrl: serialized.src,
        originalSecureUrl: serialized.src,
        fileName: serialized.designMeta?.fileName || "Design",
        publicId: serialized.designMeta?.publicId || null,
        width: obj.width,
        height: obj.height,
      });
    }
    styleDesignObject(obj);
  }
};

export const loadCanvasJson = async (canvas, json, { lockHistory } = {}) => {
  if (!canvas || !json) return;
  lockHistory?.(true);
  try {
    const parsed = typeof json === "string" ? JSON.parse(json) : json;
    await canvas.loadFromJSON(parsed, restoreGangDesignProps);
    canvas.backgroundColor = "transparent";
    canvas.getObjects().forEach((obj) => {
      restoreGangDesignProps(
        {
          isGangDesign: obj.isGangDesign,
          designMeta: obj.designMeta,
          src: typeof obj.getSrc === "function" ? obj.getSrc() : obj.src,
        },
        obj,
      );
    });
    canvas.requestRenderAll();
  } finally {
    lockHistory?.(false);
  }
};

export const getSelectedDesignDpi = (object) => {
  if (!object?.designMeta) return null;
  const printWidth = uiPxToInches(object.getScaledWidth());
  const nativeWidth = object.width || 0;
  return estimateDpi(nativeWidth, printWidth);
};

export const exportCanvasDataUrl = (canvas, multiplier) => {
  if (!canvas) return null;

  const options = {
    format: "jpeg",
    quality: 0.85,
    multiplier: multiplier || 1,
    enableRetinaScaling: false,
  };

  try {
    return canvas.toDataURL(options);
  } catch (error) {
    console.error("JPEG export failed, retrying at 1x", error);
    try {
      return canvas.toDataURL({
        ...options,
        multiplier: 1,
      });
    } catch (fallbackError) {
      console.error("Canvas export failed", fallbackError);
      throw new Error(
        "Could not export the gang sheet. Re-upload designs and try again.",
      );
    }
  }
};

export default GangCanvas;
